import type { GamificationState } from '../db/gamificationCollection';

export function istDateStringClient(date: Date = new Date()): string {
  const ist = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const d = String(ist.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function readDateStringField(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return istDateStringClient((value as { toDate: () => Date }).toDate());
  }
  if (
    value &&
    typeof value === 'object' &&
    ('seconds' in value || '_seconds' in value)
  ) {
    const sec =
      typeof (value as { seconds?: number }).seconds === 'number'
        ? (value as { seconds: number }).seconds
        : typeof (value as { _seconds?: number })._seconds === 'number'
          ? (value as { _seconds: number })._seconds
          : null;
    if (sec != null) return istDateStringClient(new Date(sec * 1000));
  }
  return undefined;
}

export function readGamificationFromStudent(student: Record<string, unknown> | null | undefined): GamificationState {
  const raw = student?.gamification;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      argus_coins: 0,
      coins_lifetime_earned: 0,
      login_streak: { current: 0, longest: 0 },
      qod_streak: { current: 0, longest: 0 },
      qod_attempted_total: 0,
      qod_correct_total: 0,
      qod_accuracy_pct: 0,
      practice_sessions_total: 0,
      practice_questions_total: 0,
      practice_correct_total: 0,
      practice_accuracy_pct: 0,
      practice_coins_earned_total: 0,
      exam_coins_earned_total: 0,
      login_streak_coins_earned_total: 0,
      qod_streak_coins_earned_total: 0,
      streak_freezes_available: 0,
      streak_break_pending: null,
      redemptions: {},
    };
  }
  const g = raw as Record<string, unknown>;
  const login = (g.login_streak ?? {}) as Record<string, unknown>;
  const qod = (g.qod_streak ?? g['qotd_streak'] ?? {}) as Record<string, unknown>;
  const qodAnsweredDate =
    readDateStringField(qod.last_answered_date) ??
    readDateStringField(g.qod_last_answered_date) ??
    readDateStringField(g['qotd_last_answered_date']);
  const attempted =
    typeof g.qod_attempted_total === 'number' && g.qod_attempted_total > 0
      ? Math.floor(g.qod_attempted_total)
      : 0;
  const correctTotal =
    typeof g.qod_correct_total === 'number' && g.qod_correct_total > 0
      ? Math.floor(g.qod_correct_total)
      : 0;
  const storedAccuracy =
    typeof g.qod_accuracy_pct === 'number' && Number.isFinite(g.qod_accuracy_pct)
      ? g.qod_accuracy_pct
      : null;
  return {
    argus_coins: typeof g.argus_coins === 'number' ? g.argus_coins : 0,
    coins_lifetime_earned: typeof g.coins_lifetime_earned === 'number' ? g.coins_lifetime_earned : 0,
    login_streak: {
      current: typeof login.current === 'number' ? login.current : 0,
      longest: typeof login.longest === 'number' ? login.longest : 0,
      last_active_date: typeof login.last_active_date === 'string' ? login.last_active_date : undefined,
    },
    qod_streak: {
      current: typeof qod.current === 'number' ? qod.current : 0,
      longest: typeof qod.longest === 'number' ? qod.longest : 0,
      last_answered_date: qodAnsweredDate,
      last_correct_date: typeof qod.last_correct_date === 'string' ? qod.last_correct_date : undefined,
    },
    qod_attempted_total: attempted,
    qod_correct_total: correctTotal,
    qod_accuracy_pct:
      storedAccuracy ?? (attempted > 0 ? Math.round((1000 * correctTotal) / attempted) / 10 : 0),
    qod_last_answered_date: qodAnsweredDate,
    qod_last_result: (g.qod_last_result ?? g['qotd_last_result']) as GamificationState['qod_last_result'],
    practice_sessions_total:
      typeof g.practice_sessions_total === 'number' && g.practice_sessions_total > 0
        ? Math.floor(g.practice_sessions_total)
        : 0,
    practice_questions_total:
      typeof g.practice_questions_total === 'number' && g.practice_questions_total > 0
        ? Math.floor(g.practice_questions_total)
        : 0,
    practice_correct_total:
      typeof g.practice_correct_total === 'number' && g.practice_correct_total > 0
        ? Math.floor(g.practice_correct_total)
        : 0,
    practice_accuracy_pct:
      typeof g.practice_accuracy_pct === 'number' && Number.isFinite(g.practice_accuracy_pct)
        ? g.practice_accuracy_pct
        : 0,
    practice_coins_earned_total:
      typeof g.practice_coins_earned_total === 'number' && g.practice_coins_earned_total > 0
        ? Math.floor(g.practice_coins_earned_total)
        : 0,
    practice_awarded_dates_by_exam: (() => {
      const rawDates = g.practice_awarded_dates_by_exam;
      if (!rawDates || typeof rawDates !== 'object' || Array.isArray(rawDates)) return undefined;
      const out: Record<string, string> = {};
      for (const [examId, value] of Object.entries(rawDates as Record<string, unknown>)) {
        if (examId.trim() && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          out[examId.trim()] = value;
        }
      }
      return Object.keys(out).length > 0 ? out : undefined;
    })(),
    practice_last_awarded_week: typeof g.practice_last_awarded_week === 'string' ? g.practice_last_awarded_week : undefined,
    exam_coins_earned_total:
      typeof g.exam_coins_earned_total === 'number' && g.exam_coins_earned_total > 0
        ? Math.floor(g.exam_coins_earned_total)
        : 0,
    login_streak_coins_earned_total:
      typeof g.login_streak_coins_earned_total === 'number' && g.login_streak_coins_earned_total > 0
        ? Math.floor(g.login_streak_coins_earned_total)
        : 0,
    qod_streak_coins_earned_total:
      typeof g.qod_streak_coins_earned_total === 'number' && g.qod_streak_coins_earned_total > 0
        ? Math.floor(g.qod_streak_coins_earned_total)
        : 0,
    streak_freezes_available:
      typeof g.streak_freezes_available === 'number' && g.streak_freezes_available > 0
        ? Math.floor(g.streak_freezes_available)
        : 0,
    streak_break_pending: (() => {
      const rawPending = g.streak_break_pending;
      if (!rawPending || typeof rawPending !== 'object' || Array.isArray(rawPending)) return null;
      const p = rawPending as Record<string, unknown>;
      const previous =
        typeof p.previous_streak === 'number' && Number.isFinite(p.previous_streak)
          ? Math.max(0, Math.floor(p.previous_streak))
          : null;
      const brokeOn = typeof p.broke_on === 'string' && p.broke_on.trim() ? p.broke_on.trim() : null;
      if (previous == null || !brokeOn) return null;
      return { previous_streak: previous, broke_on: brokeOn };
    })(),
    redemptions: (g.redemptions ?? {}) as GamificationState['redemptions'],
  };
}

export function practiceCoinsNotEarnedMessage(reason: string | null | undefined): string | null {
  if (reason === 'daily_cap' || reason === 'weekly_cap') {
    return 'Good job finishing this practice set! You already earned Argus Coins for this practice subject today. Try a different practice subject today, or come back tomorrow for this one.';
  }
  if (reason === 'insufficient_questions') {
    return 'Answer all 10 questions in a practice set to earn Argus Coins. Coins are awarded for one completed set of each practice subject each day.';
  }
  return null;
}

/** Mirrors backend `GAMIFICATION_CONFIG.exam` + `computeExamCoins`. */
export const EXAM_COINS_BASE = 60;
export const EXAM_COINS_SCORE_BONUS_MAX = 90;
export const EXAM_COINS_PROFILE_FLAT = 60;

const EXAM_NON_SCORED_IDS = new Set(['comprehensive_personality', 'career_interest_inventory']);

/** scoreFraction is 0–1 (attempt.score). Perfect scored exam → 150. */
export function computeExamCoinsFromScore(assessmentId: string, scoreFraction: number): number {
  if (EXAM_NON_SCORED_IDS.has(assessmentId)) return EXAM_COINS_PROFILE_FLAT;
  const clamped = Math.max(0, Math.min(1, scoreFraction));
  return EXAM_COINS_BASE + Math.round(clamped * EXAM_COINS_SCORE_BONUS_MAX);
}

/** Prefer stored coins_awarded; for legacy attempts without the field, derive from score. */
export function displayExamCoinsAwarded(params: {
  assessmentId: string;
  coinsAwarded?: number | null;
  scoreFraction?: number | null;
  status?: string;
}): number | null {
  const stored = params.coinsAwarded;
  if (typeof stored === 'number' && Number.isFinite(stored) && stored >= 0) {
    return Math.floor(stored);
  }
  if (params.status !== 'completed') return null;
  if (typeof params.scoreFraction !== 'number' || !Number.isFinite(params.scoreFraction)) return null;
  return computeExamCoinsFromScore(params.assessmentId, params.scoreFraction);
}
